<template>
  <div>

    <TipBox info :title="$t('tips.promo.title')" v-if="!data.id">
      <ol>
        <li v-html="$t('tips.promo.ol_1')"></li>
        <li>
          <span v-html="$t('tips.promo.ol_2')"></span>
          <ul>
            <li v-html="$t('tips.promo.li_1')"></li>
            <li v-html="$t('tips.promo.li_2')"></li>
            <li v-html="$t('tips.promo.li_3')"></li>
            <li v-html="$t('tips.promo.li_4')"></li>
          </ul>
        </li>
        <li v-html="$t('tips.promo.ol_3')"></li>
      </ol>
    </TipBox>

    <Panel :title="data.id ? $t('promotions.edit') : $t('promotions.new')">
      <div class="tutorial-section" :class="{finished}">
        <div class="label">{{ $t("promotions.tutorial.name_description") }}</div>
        <div class="columns">
          <div class="column">
            <div class="group">
              <LocalizedInput :title="$t('name')" :translate="data.name" />
            </div>
          </div>
          <div class="column">
            <div class="group">
              <LocalizedInput :title="$t('description')" :translate="data.description" />
            </div>
          </div>
        </div>
      </div>

      <div class="tutorial-section" :class="{hide: !hasNameAndDescription, finished}">
        <div class="columns">
          <div class="column">
            <div class="group">
              <label>{{ $t("promotions.type") }}</label>
              <select v-model="data.triggerType">
                <option :value="null">{{ $t("promotions.type") }}...</option>
                <option value="">{{ $t("promotions.normal") }}</option>
                <option value="x_for_y">{{ $t("promotions.x_for_y") }}</option>
                <option value="mobile_app">{{ $t("promotions.mobile") }}</option>
                <option value="with_item">{{ $t("promotions.with") }}</option>
              </select>
            </div>
          </div>
          <div class="column">
            <div class="group" :class="{hide: data.triggerType !== 'x_for_y'}">
              <label>{{ $t("promotions.x_for_y_description_1") }}</label>
              <input type="text" v-model="data.triggerParameters.x">
            </div>
            <div class="group" :class="{hide: data.triggerType !== 'x_for_y'}">
              <label>{{ $t("promotions.x_for_y_description_2") }}</label>
              <input type="text" v-model="data.triggerParameters.y">
            </div>
            <div class="group" :class="{hide: data.triggerType !== 'x_for_y'}">
              <input id="chkHideMessage" type="checkbox" v-model="data.triggerParameters.hideReminders">
              <label for="chkHideMessage">{{ $t("promotions.hide_messages") }}</label>
            </div>
            <div class="group" :class="{hide: data.triggerType !== 'with_item'}">
              <label>{{ $t("promotions.with_description") }}</label>
              <MultiSelect :options="formattedItems" :selected="data.triggerParameters.items" :trackByAttribute="'value'"
                           @changed="data.triggerParameters.items = $event"/>
            </div>
            <div class="group" :class="{hide: data.triggerType !== 'with_item'}">
              <label>{{ $t("promotions.with_description_tag") }}</label>
              <MultiSelect :options="formattedTags" :selected="data.triggerParameters.tags" :trackByAttribute="'value'"
                           @changed="data.triggerParameters.tags = $event"/>
              <input id="chkHideItems" type="checkbox" v-model="data.triggerParameters.hideItems">
              <label for="chkHideItems">{{ $t("promotions.hide_items") }}</label>
            </div>
          </div>
        </div>
      </div>

      <div class="tutorial-section" :class="{hide: !hasType, finished}">
        <div class="label">{{ $t("promotions.tutorial.appliedOn") }}</div>
        <div class="columns">
          <div class="column">
            <div class="group">
              <label>{{ $t("promotions.apply") }}</label>
              <select v-model="data.appliedOn">
                <option value="" selected hidden>{{ $t("promotions.apply_on") }}</option>
                <option value="sale">{{ $t("promotions.total") }}</option>
                <option value="sale_after_taxes">{{ $t("promotions.total_after_taxes") }}</option>
                <option value="items">{{ $t("promotions.multiple_items") }}</option>
                <option value="one_item">{{ $t("promotions.one_item") }}</option>
              </select>
            </div>
          </div>
          <div class="column">
            <div class="group" :class="{hide: data.appliedOn !== 'items' && data.appliedOn !== 'one_item'}">
              <label>{{ $t("promotions.applicable_tags") }}</label>
              <MultiSelect :options="formattedTags" :selected="data.inclusion.tags" :trackByAttribute="'value'"
                           @changed="data.inclusion.tags = $event"/>
            </div>
            <div class="group" :class="{hide: !hasAppliedOn}">
              <label>{{ $t("promotions.exclusion_tags") }}</label>
              <MultiSelect :options="formattedTags" :selected="data.exclusion.tags" :trackByAttribute="'value'"
                           @changed="data.exclusion.tags = $event"/>
            </div>
          </div>
        </div>
      </div>

      <div class="tutorial-section" :class="{hide: !hasAppliedOn, finished}">
        <div class="label">{{ $t("promotions.tutorial.reduction") }}</div>
        <div class="columns">
          <div class="column">
            <div class="group">
              <label>{{$t('reward_type')}}</label>
              <select v-model="data.rewardType">
                <option value="" selected hidden>{{ $t("promotions.reduction_type") }}</option>
                <option value="%">{{ $t("percent") }}</option>
                <option value="$">{{ $t("flat") }}</option>
                <option value="replace">{{ $t("replace") }}</option>
                <option value="hidden_product" >{{$t("hidden_product")}}</option>
              </select>
            </div>
          </div>
          <div class="column">
            <div class="group" :class="{hide: !data.rewardType || data.rewardType == 'hidden_product'}">
              <label v-if="data.rewardType === '%'">{{ $t("promotions.percent_label") }}</label>
              <label v-if="data.rewardType === '$'">{{ $t("promotions.flat_label") }}</label>
              <label v-if="data.rewardType === 'replace'">{{ $t("promotions.replace_label") }}</label>
              <input type="text" v-model="data.rewardAmount">
            </div>
          </div>
        </div>
      </div>

      <div class="tutorial-section" :class="{hide: !hasPromoTypeAndAmount, finished}">
        <div class="label hideWhenFinished">{{ $t("promotions.tutorial.constraint") }}</div>
        <div class="group hideWhenFinished">
          <input id="chkConstraintsYes" type="radio" name="chkConstraints" v-model="showConstraints" :value="true">
          <label for="chkConstraintsYes">{{ $t("yes") }}</label>
          <input id="chkConstraintsNo" type="radio" name="chkConstraints" v-model="showConstraints" :value="false">
          <label for="chkConstraintsNo">{{ $t("no") }}</label>
        </div>
        <div class="sublabel" :class="{hide: !showConstraints && !finished}">{{ $t("promotions.tutorial.choose_constraint") }}</div>
        <div class="columns" :class="{hide: !showConstraints && !finished}">
          <div class="column">
            <div class="group">
              <label>{{ $t("promotions.amount_constraint") }}</label>
              <select v-model="data.usageRestriction">
                <option value="" selected hidden>{{ $t("promotions.amount") }}</option>
                <option value="unlimited">{{$t('no_limit')}}</option>
                <option value="once_per_user">{{$t('once_per_user')}}</option>
                <option value="on_first_order">{{$t('on_first_order')}}</option>
                <option value="once_ever">{{$t('unique_use')}}</option>
              </select>
            </div>
          </div>
          <div class="column">
            <div class="group" :class="{hide: !data.usageRestriction && !finished}">
              <label>{{ $t("minimum_order_total") }}</label>
              <input type="number" v-model="data.minAmount">
            </div>
          </div>
          <div class="column">
            <div class="group" :class="{hide: data.minAmount == null}">
              <label>{{ $t("promotions.disable_for") }}</label>
              <input id="chkDisableTakeout" type="checkbox" v-model="disableFor.takeout"><label for="chkDisableTakeout">{{ $t("deliveryMethod.takeout") }}</label>
              <input id="chkDisableDelivery" type="checkbox" v-model="disableFor.delivery"><label for="chkDisableDelivery">{{ $t("deliveryMethod.delivery") }}</label>
            </div>
          </div>
        </div>
      </div>

      <div class="tutorial-section" :class="{hide: !hasFinishedConstraints, finished}">
        <div class="label question">{{ $t("promotions.tutorial.apply") }}</div>
        <div class="group">
          <input id="chkApplyAuto" type="radio" name="chkApply" v-model="showCode" :value="false">
          <label for="chkApplyAuto">{{ $t("promotions.auto") }}</label>
          <input id="chkApplyCode" type="radio" name="chkApply" v-model="showCode" :value="true">
          <label for="chkApplyCode">{{ $t("promotions.with_code") }}</label>
        </div>
        <div class="group" :class="{hide: !showCode}">
          <div class="group">
            <label>{{$t('promo_code')}}</label>
            <input type="text" v-model="data.code">
          </div>
        </div>
      </div>

      <div class="tutorial-section" :class="{hide: !hasFinishedApply, finished}">
        <div class="label question hideWhenFinished">{{$t('promotions.promo_begin_end')}}</div>
        <div class="columns">
          <div class="column">
            <div class="group">
              <label>{{$t('promotions.begin')}}</label>
              <datetime type="datetime" v-model="data.startAt" :use12-hour="hourFormat == '12'"/>
            </div>
          </div>
          <div class="column">
            <div class="group">
              <label>{{$t('promotions.end')}}</label>
              <datetime type="datetime" v-model="data.endAt" :use12-hour="hourFormat == '12'"/>
            </div>
          </div>
        </div>
      </div>

      <div class="tutorial-section" :class="{hide: !hasFinishedDates, finished}">
        <div class="label question hideWhenFinished">{{$t('promotions.link_promo')}}</div>
        <div class="group hideWhenFinished">
          <input id="chkPosYes" type="radio" name="chkPos" v-model="showPos" :value="true">
          <label for="chkPosYes">{{ $t("yes") }}</label>
          <input id="chkPosNo" type="radio" name="chkPos" v-model="showPos" :value="false">
          <label for="chkPosNo">{{ $t("no") }}</label>
        </div>
        <div class="group" :class="{hide: !showPos && !finished}">
          <label>{{$t('external_id')}}</label>
          <input type="text" v-model="data.externalId">
        </div>
        <div class="group" :class="{hide: !finished}">
          <ImageUpload identifier="promoImage" :previewHeight="150" :object="data.image" @changed="data.image = $event" :maxSizeInMb="0.8"></ImageUpload>
        </div>
      </div>

      <Panel class="schedule" :title="$t('promotions.schedule')" v-if="hasFinishedPos" collapsible>
        <div class="description">
          <span>{{$t('promotions.schedule_explanation')}}</span>
          <button class="btn btn-primary" v-if="!data.schedule" @click="createSchedule()">{{$t('promotions.schedule_add')}}</button>
        </div>
        <template v-if="data.schedule">
          <br>
          <div class="columns" v-if="data.schedule">
            <div class="column">
              <DayTimeRange v-for="(day, dayIndex) in data.schedule.slice(0,4)" :key="dayIndex" :day="day" :dayName="day.name"></DayTimeRange>
            </div>
            <div class="column">
              <DayTimeRange v-for="(day, dayIndex) in data.schedule.slice(4, 7)" :key="dayIndex" :day="day" :dayName="day.name"></DayTimeRange>
            </div>
          </div>
        </template>
      </Panel>

      <Panel class="companies" :title="$t('promotions.companies')" v-if="hasFinishedPos && branchCompanies.length > 0 && !companyId" collapsible>
        <div class="description">
          {{$t('promotions.companies_description')}}
          <div class="disclaimer" v-if="data.companies && data.companies.length == 0">{{$t('promotions.companies_description_disclaimer')}}</div>
        </div>
        <ListMultiSelect :bank="branchCompanies" :selected="data.companies"
                         show-select-all show-unselect-all
                         :bank-title="$t('promotions.branch_companies').replace('{INVENTORY}', $to($store.state.contextInventory.name))"
                         :selected-title="$t('promotions.selected_branch_companies')"></ListMultiSelect>
      </Panel>

      <div class="tutorial-section" :class="{hide: !hasFinishedPos, finished}">
        <div class="label">{{ $t("promotions.tutorial.ready") }}</div>
      </div>

      <div class="tutorial-section buttons" :class="{hide: !hasFinishedPos, finished}" style="border-top: 0;padding-top: 0;" v-if="!finished">
        <button class="btn btn-primary" @click="save">{{$t("save")}}</button>
      </div>

    </Panel>

    <div class="footer-actions" v-if="finished">
      <template v-if="$store.getters.loggedUserHasModifyPermission">
        <button class="btn btn-primary" @click="save">{{$t("save")}}</button>
        <button class="btn btn-secondary" @click="deletePromo" style="margin-left: 20px;" v-if="finished">{{$t("datatable.delete")}}</button>
      </template>
      <div class="grow"></div>
      <button class="btn btn-primary" @click="viewUsage" v-if="data.id">{{$t("promotion_usage.usage_stats")}}</button>
    </div>

    <Modal
      name="delete-confirmation"
      :title="$t('promotions.confirmation')"
      dismissable
      :actions="[{name: $t('back'), action: 'close'}, {name: $t('datatable.delete'), action: deletePromotionAction, primary: true}]">
      <p>{{$t('promotions.delete_message')}}?</p>
    </Modal>

  </div>
</template>

<script>
  import LocalizedInput from "@/components/tools/LocalizedInput";
  import ListMultiSelect from "@/components/tools/ListMultiSelect";
  import DayTimeRange from '@/components/tools/DayTimeRange.vue';
  import ImageUpload from '@/components/tools/ImageUpload.vue';
  import MultiSelect from '@/components/tools/MultiSelect.vue';
  import { mapActions } from 'vuex';
  import { Core } from "@/core/Core";
  import moment from "moment-timezone";

  export default {
    name: "promotionEditLegacy",

    components: { MultiSelect, LocalizedInput, ListMultiSelect, ImageUpload, DayTimeRange },

    data() {
      return {
        tags: [],
        branchCompanies: [],
        inventoryItems: [],
        inventory: null,
        showConstraints: null,
        showCode: null,
        showPos: null,
        finished: false,
        disableFor: {
          delivery: false,
          takeout: false
        },
        data: {
          id: null,
          companies: [],
          externalId: null,
          image: { url: "" },
          name: {
            fr: null
          },
          description: {
            fr: null
          },
          inclusion: {
            tags: []
          },
          exclusion: {
            tags: []
          },
          rewardType: "",
          rewardAmount: null,
          minAmount: null,
          schedule: null,
          usageRestriction: "",
          triggerType: null,
          triggerParameters: {
            x: null,
            y: null,
            hideReminders: false,
            tags: [],
            hideItems: false,
            items: []
          },
          startAt: null,
          endAt: null,
          active: true,
          deleted: false,
          code: null,
          appliedOn: null,
          disableForMethod: []
        }
      }
    },

    async mounted() {
      this.$store.commit('showSpinner', true);
      this.$store.state.pageTitleTranslationKey = "promotions.title";
      this.$store.state.pageIcon = "/icon/promos.svg";
      this.inventory = await this.$store.getters.getContextInventory;
      await this.loadBranchCompanies();
      await this.loadPromotions();
      
      if (!this.$store.getters.loggedUserHasModifyPermission) {
        window.lockFields();
      }
    },

    methods: {
      ...mapActions(["getPromotion", "savePromotion", "createPromotion", "getManyInventoryEntity", "deletePromotion"]),

      async loadBranchCompanies() {
        if (this.companyId) {
          this.branchCompanies = [];
          this.data.companies = [];
        } else {
          this.branchCompanies = await Core.request('GET', '/api/entity/company/select', {branch: this.branchId});
          if (this.branchCompanies.length > 0) {
            this.branchCompanies = this.branchCompanies.filter(c => c.inventory == this.inventory.id);
          }
        }
      },

      async loadPromotions() {
        Promise
          .all([
            this.$route.query.id ? this.getPromotion({inventory: this.inventory.id, id: this.$route.query.id}) : Promise.resolve(),
            this.getManyInventoryEntity({entity: "tags", inventory: this.inventory.id}),
            this.getManyInventoryEntity({entity: "items", inventory: this.inventory.id}),
          ])
          .then(([promotion, inventoryTags, inventoryItems]) => {
            this.tags = inventoryTags;
            this.inventoryItems = inventoryItems;

            if (promotion) {
              this.data = promotion;

              if (this.data.startAt) {
                this.data.startAt = moment(this.data.startAt, "YYYY-MM-DD HH:mm:ss").format();
              }

              if (this.data.endAt) {
                this.data.endAt = moment(this.data.endAt, "YYYY-MM-DD HH:mm:ss").format();
              }

              this.data.triggerParameters = this.data.triggerParameters || {};
              this.fixDynamicFieldsForTrigger(this.data.triggerType);
              this.data.inclusion.tags = this.data.inclusion.tags
                ? this.tags.filter(c => this.data.inclusion.tags.indexOf(c.id) > -1)
                : [];
              this.data.exclusion.tags = this.data.exclusion.tags
                ? this.tags.filter(c => this.data.exclusion.tags.indexOf(c.id) > -1)
                : [];
              this.data.triggerParameters.tags = this.data.triggerParameters.tags
                ? this.tags.filter(c => this.data.triggerParameters.tags.indexOf(c.id) > -1)
                : [];
              this.finished = true;
              this.showConstraints = true;
              this.showCode = !!this.data.code;
              if (!this.companyId) {
                this.data.companies = this.branchCompanies.filter(c => this.data.companies.indexOf(c.id) > -1);
              }
              this.data.inclusion.tags = this.formatObjectsForSelect(this.data.inclusion.tags);
              this.data.exclusion.tags = this.formatObjectsForSelect(this.data.exclusion.tags);
              this.data.triggerParameters.tags = this.formatObjectsForSelect(this.data.triggerParameters.tags);
              this.data.triggerParameters.items = this.formatObjectsForSelect(inventoryItems.filter(i => (this.data.triggerParameters.items || []).indexOf(i.id) > -1));
              this.disableFor.takeout = this.data.disableForMethod.indexOf("takeout") > -1;
              this.disableFor.delivery = this.data.disableForMethod.indexOf("delivery") > -1;
            }
          })
          .catch(e => {
            console.log(e)
          })
          .finally(() => {
            this.$store.commit('showSpinner', false);
          });
      },

      async save() {
        let copy = this._formatBeforeSave(this.data);
        this.$store.commit("showSpinner", true);

        if (copy.id) {
          let response = await this.savePromotion({inventory: this.inventory.id, id: copy.id, data: copy});
          if (!response) {
            toast(this.$t("promotions.error.save"), "error");
            this.$store.commit("showSpinner", false);
            return;
          }
        } else {
          let response = await this.createPromotion({inventory: this.inventory.id, data: copy});
          if (!response) {
            toast(this.$t("promotions.error.save"), "error");
            this.$store.commit("showSpinner", false);
            return;
          }
        }

        this.$store.commit("showSpinner", false);
        this.$router.push(this.$router.buildContext("/promotions"));
        toast(this.$t("promotions.success"), "success");
      },

      createSchedule() {
        this.data.schedule = [
          { name: "monday", hours: [] },
          { name: "tuesday", hours: [] },
          { name: "wednesday", hours: [] },
          { name: "thursday", hours: [] },
          { name: "friday", hours: [] },
          { name: "saturday", hours: [] },
          { name: "sunday", hours: [] }
        ]
      },

      formatObjectsForSelect(array) {
        return array.map(t => {
          return {
            id: t.id,
            label: t.name,
            value: t.id
          }
        });
      },

      viewUsage() {
        this.$router.push({ path: '/promotions/usage', query : {id: this.data.id, branch: this.branchId, company: this.companyId} });
      },

      _formatBeforeSave(data) {
        let copy = JSON.parse(JSON.stringify(data));

        copy.image = getImageData(data.image);

        if (copy.startAt) {
          copy.startAt = moment(copy.startAt).format("YYYY-MM-DD HH:mm:ss");
        }
        if (copy.endAt) {
          copy.endAt = moment(copy.endAt).format("YYYY-MM-DD HH:mm:ss");
        }
        if (!copy.schedule || this.isPromotionScheduleEmpty) {
          copy.schedule = null;
        }
        if (copy.inclusion.tags) {
          copy.inclusion.tags = copy.inclusion.tags.map(x => x.id);
        }
        if (copy.exclusion.tags) {
          copy.exclusion.tags = copy.exclusion.tags.map(x => x.id);
        }
        if (copy.rewardType === 'hidden_product') {
          copy.rewardAmount = 0;
        }

        copy.minAmount = copy.minAmount || 0;
        if (copy.triggerParameters.x) {
          copy.triggerParameters.x = parseInt(copy.triggerParameters.x);
        }
        if (!this.companyId) {
          copy.companies =  copy.companies.map(x => x.id);
        } else {
          delete copy.companies;
        }
        if (copy.triggerParameters.y) {
          copy.triggerParameters.y = parseInt(copy.triggerParameters.y);
        }
        if (copy.triggerParameters.items) {
          copy.triggerParameters.items = copy.triggerParameters.items.map(x => x.id);
        }
        if (copy.triggerParameters.tags) {
          copy.triggerParameters.tags =  copy.triggerParameters.tags.map(x => x.id);
        }
        copy.rewardAmount = parseFloat(String(copy.rewardAmount).replace(",", "."));

        // Disable for methods
        copy.disableForMethod = [];
        if (this.disableFor.takeout) {
          copy.disableForMethod.push("takeout");
        }
        if (this.disableFor.delivery) {
          copy.disableForMethod.push("delivery");
        }
        return copy;
      },

      fixDynamicFieldsForTrigger(triggerType) {
        if (triggerType === 'x_for_y') {
          if (!this.data.inclusion || typeof this.data.inclusion.length !== "undefined") {
            this.data.inclusion = {};
          }
          if (!this.data.exclusion || typeof this.data.exclusion.length !== "undefined") {
            this.data.exclusion = {};
          }
        } else if (triggerType === 'mobile_app') {

        }
      },

      deletePromo() {
        this.$modal.show("delete-confirmation");
      },

      async deletePromotionAction() {
        this.$store.commit("showSpinner", true);
        let response = await this.deletePromotion({inventory: this.inventory.id, id: this.data.id });
        if (response) {
          this.$modal.show("delete-confirmation", false);
          this.$store.commit("showSpinner", false);
          this.$router.push(this.$router.buildContext("/promotions"));
        } else {
          this.$modal.show("delete-confirmation", false);
          toast(this.$t("error.no_server"),"error");
          this.$store.commit("showSpinner", false);
        }
      }

    },

    watch: {

      'data.triggerType': function (value) {
        this.fixDynamicFieldsForTrigger(value);
      },

      "data.appliedOn": function(value) {
        if (value === "sale") {
          this.data.inclusion.tags = [];
        }
      },

      showCode: function() {
        if (!this.showCode) {
          this.data.code = null;
        }
      }

    },

    computed: {

      hourFormat() {
        if (this.$store.state.contextCompany) {
          return this.$store.state.contextCompany.hourFormat;
        }
        return "24";
      },

      branchId() {
        return this.$route.query.branch;
      },

      companyId() {
        return this.$route.query.company;
      },

      isPromotionScheduleEmpty() {
        if (!this.data.schedule) {
          return true;
        }
        let notEmptyDays = this.data.schedule.filter(d => d.hours.length > 0 && d.hours.filter(h => !!h.from || !!h.to).length > 0);
        return notEmptyDays.length === 0;
      },

      hasNameAndDescription() {
        if (this.finished) {
          return true;
        }
        return !!this.data.name.fr || !!this.data.name.en;
      },

      hasPromoTypeAndAmount() {
        if (this.finished) {
          return true;
        }
        return !!this.data.rewardType && (this.data.rewardAmount != null || this.data.rewardType === 'hidden_product');
      },

      hasFinishedConstraints() {
        if (this.finished) {
          return true;
        }
        return typeof this.showConstraints == "boolean" && (!this.showConstraints || (
          !!this.data.usageRestriction && this.data.minAmount != null
        ));
      },

      hasFinishedApply() {
        if (this.finished) {
          return true;
        }
        return typeof this.showCode == "boolean" && (!this.showCode || this.data.code != null);
      },

      hasFinishedDates() {
        if (this.finished) {
          return true;
        }
        return this.data.startAt && this.data.endAt;
      },

      hasFinishedPos() {
        if (this.finished) {
          return true;
        }
        return typeof this.showPos == "boolean" && (!this.showPos || (
          !!this.data.externalId
        ))
      },

      hasAppliedOn() {
        if (this.finished) {
          return true;
        }
        return this.data.appliedOn != null && (this.data.appliedOn === "sale" || this.data.appliedOn === "sale_after_taxes" || this.data.inclusion.tags.length > 0)
      },

      hasType() {
        if (this.finished) {
          return true;
        }
        let xy = this.data.triggerType !== "x_for_y" || (
          this.data.triggerParameters.x && this.data.triggerParameters.y
        );
        let withItem = this.data.triggerType !== "with_item" || this.data.triggerParameters.items.length > 0 || this.data.triggerParameters.tags.length > 0;
        return typeof this.data.triggerType == "string" && xy && withItem;
      },

      formattedTags() {
        return this.tags.map(t => {
          return {
            id: t.id,
            label: t.name,
            value: t.id
          };
        });
      },

      formattedItems() {
        return this.inventoryItems.map(i => {
          return {
            id: i.id,
            label: i.name,
            value: i.id
          }
        });
      }

    }
  }
</script>

<style lang="scss" scoped>
  input {
    width: 100%;
  }
  .tutorial-section {
    position: relative;
    padding-left: 33px;
    &:not(:first-of-type) {
      padding-top: 28px;
      border-top: 1px solid #e5e5e5;
    }
    &:not(:last-of-type) {
      margin-bottom: 24px;
    }
    .label, .sublabel {
      color: #444;
      margin-bottom: 16px;
      font-size: 18px;
      opacity: 1;
      &.label {
        margin-left: -22px;
        &:before {
          content: "►";
          position: relative;
          left: -12px;
          color: var(--primary-color);
        }
      }
    }
    &.finished {
      padding-left: 0;
      padding-top: 0;
      border-top: none;
      .label, .sublabel {
        display: none;
      }
      .label.question {
        display: block;
        margin-left: 0;
        &:before {
          display: none;
        }
      }
      .hideWhenFinished {
        display: none !important;
      }
    }
  }
  .label, .sublabel, .columns, .group, .tutorial-section {
    transition: opacity 0.4s ease;
    &.hide {
      max-height: 0;
      overflow: hidden;
      opacity: 0;
      margin: 0;
      padding: 0;
    }
  }

  .companies .description {
    margin-bottom: 20px;
    .disclaimer {
      padding-top: 10px;
      font-weight: bold;
      color: red;
    }
  }
  .hr {
    border-bottom: 1px solid #eee;
    margin: 20px 0;
    width: 100%;
  }

  .buttons {
    display: flex;
    width: 100%;

    .grow {
      flex-grow: 1;
    }
  }

  .schedule {
    .description {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
</style>

<style lang="scss">

  .tutorial-section .vdatetime-input {
    width: 100%;
  }

</style>
